import { create, v, w } from '@dojo/framework/core/vdom';
import { theme } from '@dojo/widgets/middleware/theme';
// import { focus } from '@dojo/framework/core/middleware/focus';
import { formatAriaProperties } from '@dojo/widgets/common/util';

import { NotificationIcon } from './notification-icon';
import css from '../css/speed-dial.m.css';

export interface ButtonProperties {
	/** Custom aria attributes */
	aria?: { [key: string]: string | null };
	/** Whether the button should receive focus */
	shouldFocus?: boolean;
	/** Whether the button is disabled or clickable */
	disabled?: boolean;
	/** The name of the button */
	name?: string;
	/** Handler for events triggered by button losing focus */
	onBlur?(): void;
	/** Handler for events triggered by a button click */
	onClick?(event: MouseEvent): void;
	/** Handler for events triggered by a key down */
	onKeyDown?(event: KeyboardEvent): void;
	/** Handler for events triggered by "on down" */
	onDown?(): void;
	/** Handler for events triggered by "on focus" */
	onFocus?(): void;
	/** Handler for events triggered by "on out" */
	onOut?(): void;
	/** Handler for events triggered by "on over" */
	onOver?(): void;
	/** Handler for events triggered by "on up" */
	onUp?(): void;
	/** Indicates status of a toggle button */
	expanded?: boolean;
	/** Button type can be "submit", "reset", "button", or "menu" */
	type?: 'submit' | 'reset' | 'button' | 'menu';
	/**  Defines a value for the button submitted with form data */
	value?: string;
	/** `id` set on the root button DOM node */
	widgetId?: string;
	/** The title text for the button node */
	title?: string;
	/**  */
	size?: 'small' | 'large';
	isAction?: boolean;
	isHighlighted?: boolean;
}

const factory = create({ theme }).properties<ButtonProperties>();

export const ActionButton = factory(({ id, children, properties, middleware: { theme } }) => {
	const {
		aria = {},
		shouldFocus,
		disabled,
		widgetId,
		name,
		expanded,
		type = 'button',
		value,
		onClick,
		onOut,
		onOver,
		onDown,
		onKeyDown,
		onUp,
		onBlur,
		onFocus,
		title,
		size = 'large',
		isAction = false,
		isHighlighted = false
	} = properties();

	const idBase = widgetId || `button-${id}`;
	const themeCss = theme.classes(css);
	const buttonSize = size === 'large' ? themeCss.large : themeCss.small;
	const actionStyle = isAction ? [themeCss.action, themeCss.actionTransition] : [];

	aria['expanded'] = typeof expanded === 'boolean' ? expanded.toString() : null;
	return [
		v(
			'button',
			{
				key: `${id}`,
				classes: [themeCss.button, buttonSize, ...actionStyle],
				title: title,
				disabled: disabled,
				id: idBase,
				focus: shouldFocus,
				name: name,
				type: type,
				value: value,
				onblur: () => onBlur && onBlur(),
				onclick: (event) => {
					onClick && onClick(event);
				},
				onfocus: () => onFocus && onFocus(),
				onpointerenter: () => onOver && onOver(),
				onpointerleave: () => onOut && onOut(),
				onpointerdown: () => onDown && onDown(),
				onpointerup: () => onUp && onUp(),
				onkeydown: (event) => onKeyDown && onKeyDown(event),
				...formatAriaProperties(aria)
			},
			[v('span', { classes: [themeCss.label] }, children())]
		),
		isHighlighted ? w(NotificationIcon, {}) : null
	];
});
